import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { __OPERATION__EVENT__ADMIN__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import FiltersEventRegistration from 'system/components/event-registration/list';
import { HttpResponseList } from 'shell/models/http-response';
import { FiltersFormEventRegistration, EventRegistrationClient } from './types';
import { useFormik } from 'formik';
import { initialValues } from '..';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import { generatePath, useNavigate } from 'react-router-dom';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonEventRegistrationCrumbs } from '../constants';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { authsObj, headCells, validationSchemaEventRegistrationFilter } from './constants';
import { Typography, useEventCallback } from '@mui/material';
import ListHeader from 'shell/components/shared/list-header';
import {
  EventRegistrationsResponse,
  transformerEventRegistrations,
} from '../utils/transform-event-registrations';
import { EventRegistration } from 'shell/models/event-registration';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { AxiosPromise } from 'axios';
import { transformDataset } from './utils';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';

interface OperatorModal<T> extends AlertModal<T> {
  choosenElement?: string;
}

const EventRegistrationPage = (): React.ReactElement => {
  const [deleteAlertModal, setDeleteAlertModal] = useState<OperatorModal<EventRegistrationClient>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<EventRegistrationClient>>(
    {
      element: null,
      open: false,
    }
  );

  const filterTable = useFilterTable<FiltersFormEventRegistration>();

  const [eventRegistrations, getEventRegistrations] = useHttpFetch<
    HttpResponseList<EventRegistration>,
    EventRegistrationsResponse
  >(`${__OPERATION__EVENT__ADMIN__}/iscrizioneEvento/list`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: transformerEventRegistrations,
  });

  const deleteEventRegistration = useHttpClient(`${__OPERATION__EVENT__ADMIN__}/iscrizioneEvento`, {
    options: {
      method: 'DELETE',
    },
  });

  const onSubmit = useCallback(
    (formValues: FiltersFormEventRegistration) => {
      filterEventRegistrationsForm.setFieldValue('page', 0);
      const queryString = transformDataset({ ...formValues, page: 0 });
      getEventRegistrations({
        params: {},
        paramsSerializer: () => queryString,
      });
    },
    [getEventRegistrations]
  );

  const filterEventRegistrationsForm = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchemaEventRegistrationFilter,
    onSubmit,
  });

  useEffect(() => {
    filterEventRegistrationsForm.setValues(filterTable.values || initialValues);
    const queryString = transformDataset(filterTable.values || initialValues);
    getEventRegistrations({
      params: {},
      paramsSerializer: () => queryString,
    });
  }, []);

  const count: number = useMemo(() => {
    return (eventRegistrations.data as EventRegistrationsResponse)?.pageInfo?.totalCount || 0;
  }, [eventRegistrations.data]);

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const onVisibilityPress = useCallback(
    (cell): Promise<EventRegistrationClient> => {
      return new Promise((resolve) => {
        navigate(
          generatePath(
            '/sistema/eventmanager/iscrizione-evento/dettaglio/:codiceSottoscrittore/:codiceTipoDestinazione/:codiceTipoEvento/:progressivo',
            {
              codiceSottoscrittore: cell.subscriberCode,
              codiceTipoDestinazione: cell.destinationCode,
              codiceTipoEvento: cell.eventCode,
              progressivo: cell.progressive,
            }
          )
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell): Promise<EventRegistrationClient> => {
      return new Promise((resolve) => {
        navigate(
          generatePath(
            '/sistema/eventmanager/iscrizione-evento/modifica/:codiceSottoscrittore/:codiceTipoDestinazione/:codiceTipoEvento/:progressivo',
            {
              codiceSottoscrittore: cell.subscriberCode,
              codiceTipoDestinazione: cell.destinationCode,
              codiceTipoEvento: cell.eventCode,
              progressivo: cell.progressive,
            }
          )
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashPress = useEventCallback(
    (cell: EventRegistrationClient): Promise<EventRegistrationClient> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<EventRegistrationClient>;
    }
  );

  const handleChangePage = useCallback(
    (newPage: number) => {
      const newDataset = { ...filterEventRegistrationsForm.values, page: newPage || 0 };
      const queryString = transformDataset(newDataset);
      (
        getEventRegistrations({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<EventRegistration>, EventRegistrationsResponse>
      ).then(() => {
        filterEventRegistrationsForm.setFieldValue('page', newPage);
      });
    },
    [
      getEventRegistrations,
      filterEventRegistrationsForm.values,
      filterEventRegistrationsForm.setFieldValue,
    ]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage: number) => {
      const newDataset = {
        ...filterEventRegistrationsForm.values,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };
      const queryString = transformDataset(newDataset);
      (
        getEventRegistrations({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<EventRegistration>, EventRegistrationsResponse>
      ).then(() => {
        filterEventRegistrationsForm.setValues(newDataset);
      });
    },
    [
      getEventRegistrations,
      filterEventRegistrationsForm.setFieldValue,
      filterEventRegistrationsForm.values,
    ]
  );

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterEventRegistrationsForm.values, orders };
      const queryString = transformDataset(newDataset);
      (
        getEventRegistrations({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<EventRegistration>, EventRegistrationsResponse>
      ).then(() => {
        filterEventRegistrationsForm.setFieldValue('orders', orders);
      });
    },
    [
      getEventRegistrations,
      filterEventRegistrationsForm.values,
      filterEventRegistrationsForm.setValues,
    ]
  );

  const onCloseDeleteSuccessModal = useEventCallback(() => {
    setDeleteSuccessModal({ open: false, element: null });
  });

  const onOkDeleteSuccessModal = useEventCallback(() => {
    setDeleteSuccessModal({ open: false, element: null });
  });

  return (
    <Fragment>
      <Breadcrumbs items={CommonEventRegistrationCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuova iscrizione evento"
        addBtnLabel="Aggiungi nuova iscrizione evento"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sistema/eventmanager/iscrizione-evento/nuovo');
        }}
        title="Gestione Iscrizione Evento"
      />
      <div className="pmi-my-3">
        <FiltersEventRegistration filterEventRegistrationForm={filterEventRegistrationsForm} />
      </div>
      {eventRegistrations.data ? (
        (eventRegistrations.data as EventRegistrationsResponse)?.eventRegistrations?.length > 0 ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterEventRegistrationsForm.values.orders}
            activeActions={auths}
            page={filterEventRegistrationsForm.values.page}
            rowsPerPage={filterEventRegistrationsForm.values.rowsPerPage}
            body={(eventRegistrations.data as EventRegistrationsResponse)?.eventRegistrations}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            count={count || 0}
            isServer
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista Servizi"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione Iscrizione Evento"
        message={`Confermare la cancellazione all'iscrizione evento ${
          deleteAlertModal.element?.eventCode || ''
        }?`}
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          (
            deleteEventRegistration({
              url: `/codiceSottoscrittore=${deleteAlertModal.element?.subscriberCode}&codiceTipoDestinazione=${deleteAlertModal?.element?.destinationCode}&codiceTipoEvento=${deleteAlertModal.element?.eventCode}&progressivo=${deleteAlertModal.element?.progressive}`,
            }) as AxiosPromise<void>
          ).then(() => {
            setDeleteAlertModal({ open: false, element: null });
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Iscrizione Evento"
        message={`La cancellazione dell'iscrizione all evento ${
          deleteSuccessModal.element?.eventCode || ''
        } è avvenuta con successo`}
        onClose={onCloseDeleteSuccessModal}
        onOk={onOkDeleteSuccessModal}
      />
    </Fragment>
  );
};

EventRegistrationPage.displayName = 'Event Registration page component';

export default EventRegistrationPage;
